<template>
  <content-dialog
    :visible.sync="modalVisible"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :small-title="$vuetify.breakpoint.xsOnly"
    max-width="1200px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <div class="title">
        Manage items and costs for the report
      </div>
      <div class="caption" />
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text :class="{'pa-4': $vuetify.breakpoint.smAndUp }">
      <validation-observer ref="validation">
        <service-cost-report-row
          v-for="task in serviceTasksDirty"
          :key="task.id"
          :task.sync="task"
          :currency="currencyDirty"
          class="list--bordered"
        />
      </validation-observer>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <div class="grow">
        <v-row class="mb-4 pr-6">
          <v-spacer />
          <span class="mr-4">
            Total:
          </span>
          <strong>{{ totalCost | currency(currencyDirty, 2) }}</strong>
        </v-row>
        <v-row>
          <v-btn
            v-if="serviceCostReportId"
            small
            outlined
            color="error"
            @click="toggleConfirmationDialog(true)"
          >
            <v-icon
              left
              small
            >
              delete
            </v-icon>
            Delete
          </v-btn>
          <v-spacer />
          <v-btn
            text
            color="grey"
            @click="modalVisible = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="submit()"
          >
            {{ serviceCostReportId ? 'Save Report' : 'Create Report' }}
          </v-btn>
        </v-row>
      </div>

      <confirm-delete-dialog
        :visible.sync="deleteConfirmationDialogVisible"
        :submitting="deleting"
        text="Are you sure you want to delete this report?"
        @confirmed="deleteReport"
      />
    </template>
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog'
import validationScrollMixin from '@/mixins/validationScrollMixin'
import ServiceCostReport from '@/models/ServiceCostReport'
import cloneDeep from 'lodash/cloneDeep'
import ServiceCostReportRow from '@/components/service/serviceCostReports/ServiceCostReportRow'
import Big from 'big.js'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog.vue'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'

export default {
    name: 'ServiceCostReportManageItemsDialog',
    components: {
      ConfirmDeleteDialog,
      ServiceCostReportRow,
      ContentDialog
    },
    mixins: [
      validationScrollMixin,
      deleteConfirmationMixin
    ],
    props: {
      visible: Boolean,
      yacht: {
        type: Object,
        default: () => undefined
      },
      serviceTasks: {
        type: Array,
        default: () => undefined
      },
      dateFrom: {
        type: String,
        default: () => undefined
      },
      dateTo: {
        type: String,
        default: () => undefined
      },
      currency: {
        type: String,
        default: 'EUR'
      },
      serviceCostReportId: {
        type: Number,
        default: undefined
      }
    },
    data() {
      return {
        serviceTasksDirty: [],
        currencyDirty: 'EUR',
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      totalCost: function () {
        if (this.serviceTasksDirty.length === 0) return 0

        return this.serviceTasksDirty
          .filter(t => t.included)
          .map(t => t.price ? Big(t.price).toNumber() : 0)
          .reduce((a, b) => (a + b), 0)
      }
    },
    watch: {
      visible: function (value) {
        if (!value) return

        this.currencyDirty = this.currency

        this.fetchReport()
      }
    },
    methods: {
      prepareServiceTasks() {
        if (this.serviceCostReportId || !this.serviceTasks) {
          this.serviceTasksDirty = []
          return
        }

        let serviceTasks = cloneDeep(this.serviceTasks)

        this.serviceTasksDirty = serviceTasks.map(t => {
          return {
            ...t,
            included: t.owner_visible
          }
        })
      },
      async submit() {
        let validation = this.$validator.validateAll()

        if (!validation) return

        this.createReport()
      },
      async createReport() {
        if (this.serviceCostReportId) {
          this.updateReport()
          return
        }

        try {
          this.submitting = true

          let data = {
            yacht_id: this.yacht.id,
            date_from: this.dateFrom,
            date_to: this.dateTo,
            currency: this.currencyDirty,
            items: this.serviceTasksDirty
              .filter(t => t.included)
              .map(t => {
                return {
                  id: t.id,
                  price: t.price,
                  description: t.description,
                  warranty_insurance_note: t.warranty_insurance_note,
                  title: t.title,
                  items: t.items
                }
              })
          }

          let result = await ServiceCostReport.api.save(data)

          this.$store.dispatch('alert', {
            text: 'Service cost report successfully created.',
            color: 'success'
          })

          let routeData = this.$router.resolve({ name: 'serviceCostReportPrint', params: { id: result.id } })
          window.open(routeData.href, '_blank')

          this.$emit('created', result)
        } catch (e) {
          if (e.response && e.response.data && e.response.status === 422) {
            this.$store.dispatch('alert', {
              text: Object.values(e.response.data.errors).join(' '),
              color: 'error'
            })
            return
          }

          this.$store.dispatch('alert', {
            text: 'Something went wrong while creating the report!',
            color: 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      async updateReport() {
        try {
          this.submitting = true

          let data = {
            items: this.serviceTasksDirty
              .filter(t => t.included)
              .map(t => {
                return {
                  id: t.id,
                  price: t.price,
                  description: t.description,
                  warranty_insurance_note: t.warranty_insurance_note,
                  title: t.title,
                  items: t.items
                }
              })
          }

          let result = await ServiceCostReport.api.save(data, this.serviceCostReportId)

          this.$store.dispatch('alert', {
            text: 'Service cost report successfully updated.',
            color: 'success'
          })

          let routeData = this.$router.resolve({ name: 'serviceCostReportPrint', params: { id: result.id } })
          window.open(routeData.href, '_blank')

          this.$emit('updated', result)

          this.modalVisible = false
        } catch (e) {
          if (e.response && e.response.data && e.response.status === 422) {
            this.$store.dispatch('alert', {
              text: Object.values(e.response.data.errors).join(' '),
              color: 'error'
            })
            return
          }

          this.$store.dispatch('alert', {
            text: 'Something went wrong while updating the report!',
            color: 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      async fetchReport() {
        this.serviceTasksDirty = []

        if (!this.serviceCostReportId) {
          this.prepareServiceTasks()
          return
        }

        try {
          let result = await ServiceCostReport.api.find(this.serviceCostReportId)

          this.currencyDirty = result.currency

          this.serviceTasksDirty = result.items.map(item => {
            return {
              ...item,
              included: true
            }
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong while fetching the report!',
            color: 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      async deleteReport() {
        try {
          this.deleting = true

          await ServiceCostReport.api.destroy(this.serviceCostReportId)

          this.$store.dispatch('alert', {
            text: 'Succcessfully deleted!',
            color: 'success'
          })

          this.modalVisible = false

          this.$emit('report:deleted', this.serviceCostReportId)
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong while deleting the report!',
            color: 'error'
          })
          throw e
        } finally {
          this.deleting = false
        }
      }

    }
  }
</script>
